import Grid from '@mui/material/Grid';
import AllergiesCard from 'pages/Dashboard/components/AllergiesCard';
import FamilyHistoryCard from 'pages/Dashboard/components/FamilyHistoryCard';
import HospitalizationsCard from 'pages/Dashboard/components/HospitalizationsCard';
import LabCard from 'pages/Dashboard/components/LabCard';
import ProvidersCard from 'pages/Dashboard/components/ProvidersCard';
import PsychosocialCard from 'pages/Dashboard/components/PsychosocialCard';
import ShowMoreDrawer from 'pages/Dashboard/ShowMoreDrawer';
import * as React from 'react';
import Spinner from 'components/Common/Spinner';
import { MedicationAPIResponse, MedicationTimeLineChart, PatientMedication, PatientMedications } from 'pages/Dashboard/types/medication.types';
import { Diagnosis, DiagnosisAPIResponse } from 'pages/Dashboard/types/diagnosis.types';
import SubstanceCard from 'pages/Dashboard/components/SubstanceCard';
import SymptomsCard from 'pages/Dashboard/components/SymptomsCard';
import { UseQueryResult } from 'react-query';
import { FamilyHistoryAPIResponse } from 'pages/Dashboard/types/familyHistory.types';
import { PatientSymptomsAPIResponse } from 'pages/Dashboard/types/symptoms.types';
import { AllergiesAPIResponse } from 'pages/Dashboard/types/allergies.types';
import { HospitalizationAPIResponse } from 'pages/Dashboard/types/hospitalizations.types';
import {
  ProvidersAPIResponse,
} from 'pages/Dashboard/types/providers.types';
import { LifeStyleCategory, PatientLifeStyleAPIResponse } from 'pages/Dashboard/types/lifestyle.types';
import { PatientBackgroundAPIResponse } from 'pages/Dashboard/types/background.types';
import { LabAPIResponse } from 'pages/Dashboard/types/lab.types';
// eslint-disable-next-line import/no-cycle
import MedicationCardNew from 'pages/Dashboard/components/MedicationCardNew';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import DiagnosisCardNew from 'pages/Dashboard/components/DiagnosisCardNew';
import MedicationHistory from 'pages/Dashboard/components/MedicationHistoryCard';
import RefillsDrawer from 'pages/Dashboard/components/RefillsDrawer';
import { useParams } from 'react-router-dom';
import SyncIcon from '@mui/icons-material/Sync';
import { lightBlue } from '@mui/material/colors';
import { HrRefreshHistoryStatus } from 'pages/Dashboard/types/healthRecords.types';

// Empty state card
export function LoadingState() {
  return (
    <Box p={3} display='flex' flexDirection='row' alignItems='center' bgcolor={lightBlue[50]} borderRadius={3}>
      <SyncIcon
        color='info'
        sx={{ mr: 1,
          fontSize: 22,
          animation: 'spin 2s infinite linear',
          '@keyframes spin': {
            '0%': {
              transform: 'rotate(0deg)',
            },
            '100%': {
              transform: 'rotate(360deg)',
            },
          } }}
      />
      <Typography variant='body1' color='info.main' fontWeight={600}>
        Fetch in progress
        <Typography variant='body1' color='info.main' component='span'>
          {' - '}
          Some records are still loading, so the information shown may be incomplete.
        </Typography>
      </Typography>

    </Box>
  );
}

export const getMedicationName = ({ medication }: PatientMedication) => {
  if (medication?.standardDisplayName) {
    return medication?.standardDisplayName;
  }
  const name = medication.fullGenericName.length > 0 && medication.fullGenericName !== ''
    ? medication.fullGenericName
    : medication.fullName;
  const brandName = medication.brandName.length > 0 ? `(${medication.brandName})` : '';
  return `${name} ${brandName}`;
};

enum EDrawerItem {
  SYMPTOMS = 'Symptoms',
  LABS = 'Labs',
  MEDICATION_HISTORY = 'Medications history',
  DIAGNOSIS_HISTORY = 'Diagnoses history',
  FAMILY_HISTORY = 'Family History',
  PROVIDER_HISTORY = 'Provider history',
  HOSPITALIZATIONS = 'Hospitalizations',
  ALLERGIES = 'Allergies',
}


type MedicationFromChart = {
  medicationId: string;
  medicationName: string;
  medicationMetaId: string;
  patientId: string;
};

interface IDrawerContentProps {
  drawerItem: string;
  drawerChildProps: {
    drawerItem: string;
    closeDrawer: () => void;
    drawerMode: boolean;
  };
  drawerTabIndex: number;
  currentSymptomsResponse?: PatientSymptomsAPIResponse;
  symptomsResponse?: PatientSymptomsAPIResponse;
  labsResponse?: LabAPIResponse;
  drawerData: PatientMedication[] | Diagnosis[];
  diagnosisResponse?: DiagnosisAPIResponse;
  familyHistoryResponse?: FamilyHistoryAPIResponse;
  providerResponse?: ProvidersAPIResponse;
  hospitalizationsResponse?: HospitalizationAPIResponse;
  allergiesResponse?: AllergiesAPIResponse;
  setIsOpenedFromChart: (isOpened: boolean) => void;
  setSelectedMedication: (medication: PatientMedication) => void;
  toggleDrawer: () => void;
  onShowMoreMedication: (data: PatientMedication[], tabIndex?: number) => void;
}

function DrawerContent({
  drawerItem,
  drawerChildProps,
  drawerTabIndex,
  currentSymptomsResponse,
  symptomsResponse,
  labsResponse,
  drawerData,
  diagnosisResponse,
  familyHistoryResponse,
  providerResponse,
  hospitalizationsResponse,
  allergiesResponse,
  setIsOpenedFromChart,
  setSelectedMedication,
  toggleDrawer,
  onShowMoreMedication,
}: IDrawerContentProps) {
  switch (drawerItem) {
    case EDrawerItem.SYMPTOMS:
      return (
        <SymptomsCard
          {...drawerChildProps}
          drawerTabIndex={drawerTabIndex}
          reportedSymptoms={currentSymptomsResponse?.patientSymptoms}
          reportedSymptomsHistory={symptomsResponse?.patientSymptoms}
        />
      );
    case EDrawerItem.LABS:
      return <LabCard {...drawerChildProps} labReports={labsResponse?.labReports} />;
    case EDrawerItem.MEDICATION_HISTORY:
      return (
        <MedicationCardNew
          showTabs
          drawerTabIndex={drawerTabIndex}
          {...drawerChildProps}
          medication={drawerData as PatientMedication[]}
          setSelectedMedication={setSelectedMedication}
          setIsOpenedFromChart={setIsOpenedFromChart}
          toggleDrawer={toggleDrawer}
          onShowMoreMedication={onShowMoreMedication}
        />
      );
    case EDrawerItem.DIAGNOSIS_HISTORY:
      return (
        <DiagnosisCardNew
          showTabs
          drawerTabIndex={drawerTabIndex}
          {...drawerChildProps}
          diagnosis={diagnosisResponse?.patientDiagnosis ?? []}
        />
      );
    case EDrawerItem.FAMILY_HISTORY:
      return (
        <FamilyHistoryCard
          {...drawerChildProps}
          familyHistory={familyHistoryResponse?.familyHistory}
        />
      );
    case EDrawerItem.PROVIDER_HISTORY:
      return (
        <ProvidersCard
          {...drawerChildProps}
          patientProviders={providerResponse?.patientProviders}
        />
      );
    case EDrawerItem.HOSPITALIZATIONS:
      return (
        <HospitalizationsCard
          {...drawerChildProps}
          patientHospitalizations={hospitalizationsResponse?.patientHospitalizations}
          isNewUi
        />
      );
    case EDrawerItem.ALLERGIES:
      return (
        <AllergiesCard
          {...drawerChildProps}
          allergies={allergiesResponse?.allergies}
          isNewUi
        />
      );
    default:
      return null;
  }
}

interface IHealthRecordProps {
  diagnosis: UseQueryResult<DiagnosisAPIResponse>;
  medications: UseQueryResult<MedicationAPIResponse>;
  familyHistory: UseQueryResult<FamilyHistoryAPIResponse>;
  symptoms: UseQueryResult<PatientSymptomsAPIResponse>;
  currentSymptoms: UseQueryResult<PatientSymptomsAPIResponse>;
  allergies: UseQueryResult<AllergiesAPIResponse>;
  hospitalizations: UseQueryResult<HospitalizationAPIResponse>;
  providers: UseQueryResult<ProvidersAPIResponse>;
  lifeStyle: UseQueryResult<PatientLifeStyleAPIResponse>;
  backgrounds: UseQueryResult<PatientBackgroundAPIResponse>;
  labs: UseQueryResult<LabAPIResponse>;
  medicationChartHistory: UseQueryResult<MedicationTimeLineChart>;
  isFetchingHR: boolean;
  refreshStatus?: HrRefreshHistoryStatus;
}

export default function HealthRecordNew({
  diagnosis,
  medications,
  familyHistory,
  symptoms,
  currentSymptoms,
  allergies,
  hospitalizations,
  providers,
  lifeStyle,
  backgrounds,
  labs,
  medicationChartHistory,
  isFetchingHR,
  refreshStatus,
}: IHealthRecordProps) {
  const { id } = useParams();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { data: diagnosisResponse, isFetching: diagnosisFetching } = diagnosis;
  const { data: medicationResponse, isFetching: medicationFetching } = medications;
  const { data: currentSymptomsResponse, isFetching: currentSymptomsFetching } = currentSymptoms;
  const { data: symptomsResponse, isFetching: symptomsFetching } = symptoms;
  const { data: familyHistoryResponse, isFetching: familyHistoryFetching } = familyHistory;
  const { data: allergiesResponse, isFetching: allergiesFetching } = allergies;
  const { data: hospitalizationsResponse, isFetching: hospitalizationsFetching } = hospitalizations;
  const { data: providerResponse, isFetching: providerFetching } = providers;
  const { data: labsResponse, isFetching: labsFetching } = labs;
  const { data: lifeStyleResponse, isFetching: lifeStyleFetching } = lifeStyle;
  const { data: backgroundResponse, isFetching: backgroundFetching } = backgrounds;
  const { data: medicationChartHistoryResponse } = medicationChartHistory;

  const [drawerItem, setDrawerItem] = React.useState('');
  const [drawerData, setDrawerData] = React.useState<PatientMedication[] | Diagnosis[]>([]);
  const [drawerTabIndex, setDrawerTabIndex] = React.useState<number>(0);
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [medicationFromChart, setMedicationFromChart] = React.useState<MedicationFromChart>();
  const [isOpenedFromChart, setIsOpenedFromChart] = React.useState(false);

  const [selectedMedication, setSelectedMedication] = React.useState<Partial<PatientMedication>>();

  const closeDrawer = React.useCallback(() => {
    setDrawerItem('');
  }, [setDrawerItem]);

  const drawerChildProps = {
    drawerItem,
    closeDrawer,
    drawerMode: true,
  };

  const drawerTriggerProp = {
    drawerItem,
    closeDrawer,
    drawerMode: false,
  };

  const onShowMoreMedication = (data: PatientMedication[], tabIndex?: number) => {
    setDrawerItem(EDrawerItem.MEDICATION_HISTORY);
    setDrawerData(data);
    setDrawerTabIndex(tabIndex || 0);
  };

  const onShowMoreDiagnosis = (data: Diagnosis[], tabIndex?: number) => {
    setDrawerItem(EDrawerItem.DIAGNOSIS_HISTORY);
    setDrawerData(data);
    setDrawerTabIndex(tabIndex || 0);
  };

  const onShowMoreSymptoms = (tabIndex?: number) => {
    setDrawerItem(EDrawerItem.SYMPTOMS);
    setDrawerTabIndex(tabIndex || 0);
  };

  const toggleDrawer = () => {
    setShowDrawer((prev) => !prev);
  };

  let medicationName = '';
  if (isOpenedFromChart) {
    medicationName = medicationFromChart?.medicationName || '';
  }
  else if (selectedMedication) {
    medicationName = getMedicationName(selectedMedication as PatientMedication);
  }

  // --- Card rendering algorithm ---
  const cardOrder = [
    'medications',
    'labs',
    'medicationHistory',
    'hospitalization',
    'allergies',
    'providers',
    'diagnosis',
    'familyHistory',
    'symptoms',
    'substance',
    'psychosocial',
  ];

  const cardConfigs: Record<
    string,
    {
      key: string;
      hasData: boolean;
      component: React.ElementType;
      props: Record<string, unknown>;
      emptyState: boolean;
      isFetching: boolean;
    }
  > = {
    medications: {
      key: 'MedicationCardNew',
      hasData: (medicationResponse?.patientMedications?.length ?? 0) > 0,
      component: MedicationCardNew,
      props: {
        ...drawerTriggerProp,
        onShowMoreMedication,
        medication: medicationResponse?.patientMedications,
        showTabs: true,
        toggleDrawer,
        setSelectedMedication,
        setIsOpenedFromChart,
        isEmpty: (medicationResponse?.patientMedications?.length ?? 0) === 0,
      },
      emptyState: true,
      isFetching: medicationFetching,
    },
    labs: {
      key: 'LabCard',
      hasData: (labsResponse?.labReports?.length ?? 0) > 0,
      component: LabCard,
      props: {
        ...drawerTriggerProp,
        onShowMore: () => setDrawerItem(EDrawerItem.LABS),
        labReports: labsResponse?.labReports,
        isNewUi: true,
        isEmpty: (labsResponse?.labReports?.length ?? 0) === 0,
      },
      emptyState: true,
      isFetching: labsFetching,
    },
    medicationHistory: {
      key: 'MedicationHistoryCard',
      hasData: !isMobile
         && (medicationChartHistoryResponse?.patientMedicationsTimelines?.patientMedicationTimelines
           ?.length ?? 0) > 0,
      component: MedicationHistory,
      props: {
        medicationTimeLines:
          medicationChartHistoryResponse?.patientMedicationsTimelines as PatientMedications,
        toggleDrawer,
        setMedicationdFromChart: setMedicationFromChart,
        setIsOpenedFromChart,
      },
      emptyState: false, // Do not render if no data
      isFetching: medicationFetching,
    },
    hospitalization: {
      key: 'HospitalizationsCard',
      hasData: (hospitalizationsResponse?.patientHospitalizations?.length ?? 0) > 0,
      component: HospitalizationsCard,
      props: {
        ...drawerTriggerProp,
        onShowMore: () => setDrawerItem(EDrawerItem.HOSPITALIZATIONS),
        patientHospitalizations: hospitalizationsResponse?.patientHospitalizations,
        isNewUi: true,
        isEmpty: (hospitalizationsResponse?.patientHospitalizations?.length ?? 0) === 0,
      },
      emptyState: true,
      isFetching: hospitalizationsFetching,
    },
    allergies: {
      key: 'AllergiesCard',
      hasData: (allergiesResponse?.allergies?.length ?? 0) > 0,
      component: AllergiesCard,
      props: {
        ...drawerTriggerProp,
        onShowMore: () => setDrawerItem(EDrawerItem.ALLERGIES),
        allergies: allergiesResponse?.allergies,
        isNewUi: true,
        isEmpty: (allergiesResponse?.allergies?.length ?? 0) === 0,
      },
      emptyState: true,
      isFetching: allergiesFetching,
    },
    providers: {
      key: 'ProvidersCard',
      hasData: (providerResponse?.patientProviders?.length ?? 0) > 0,
      component: ProvidersCard,
      props: {
        ...drawerTriggerProp,
        patientProviders: providerResponse?.patientProviders,
        onShowMore: () => setDrawerItem(EDrawerItem.PROVIDER_HISTORY),
        isNewUi: true,
        noFamilyHistory: (familyHistoryResponse?.familyHistory?.length ?? 0) === 0,
      },
      emptyState: false,
      isFetching: providerFetching,
    },
    diagnosis: {
      key: 'DiagnosisCardNew',
      hasData: (diagnosisResponse?.patientDiagnosis?.length ?? 0) > 0,
      component: DiagnosisCardNew,
      props: {
        ...drawerTriggerProp,
        diagnosis: diagnosisResponse?.patientDiagnosis ?? [],
        onShowMoreDiagnosis,
        showTabs: true,
        noProviderOrFamilyHistory:
          (providerResponse?.patientProviders?.length ?? 0) === 0
          || (familyHistoryResponse?.familyHistory?.length ?? 0) === 0,
        isEmpty: (diagnosisResponse?.patientDiagnosis?.length ?? 0) === 0,
      },
      emptyState: true,
      isFetching: diagnosisFetching,
    },
    familyHistory: {
      key: 'FamilyHistoryCard',
      hasData: (familyHistoryResponse?.familyHistory?.length ?? 0) > 0,
      component: FamilyHistoryCard,
      props: {
        ...drawerTriggerProp,
        onShowMore: () => setDrawerItem(EDrawerItem.FAMILY_HISTORY),
        familyHistory: familyHistoryResponse?.familyHistory,
        isNewUi: true,
        noProvider: (providerResponse?.patientProviders?.length ?? 0) === 0,
      },
      emptyState: false,
      isFetching: familyHistoryFetching,
    },
    symptoms: {
      key: 'SymptomsCard',
      hasData:
        (currentSymptomsResponse?.patientSymptoms?.length ?? 0) > 0
        || (symptomsResponse?.patientSymptoms?.length ?? 0) > 0,
      component: SymptomsCard,
      props: {
        ...drawerTriggerProp,
        onShowMoreSymptoms,
        reportedSymptoms: currentSymptomsResponse?.patientSymptoms,
        reportedSymptomsHistory: symptomsResponse?.patientSymptoms,
        isNewUi: true,
      },
      emptyState: false,
      isFetching: currentSymptomsFetching || symptomsFetching,
    },
    substance: {
      key: 'SubstanceCard',
      hasData: (lifeStyleResponse?.patientLifeStyles?.length ?? 0) > 0,
      component: SubstanceCard,
      props: {
        lifeStyle: lifeStyleResponse?.patientLifeStyles,
        isNewUi: true,
      },
      emptyState: false,
      isFetching: lifeStyleFetching,
    },
    psychosocial: {
      key: 'PsychosocialCard',
      hasData:
        !!lifeStyleResponse?.patientLifeStyles?.find(
          (style) => style.category === LifeStyleCategory.LIFE_STYLE_CATEGORY_EXERCISE,
        ) && (backgroundResponse?.patientBackgrounds?.length ?? 0) > 0,
      component: PsychosocialCard,
      props: {
        lifeStyle: lifeStyleResponse?.patientLifeStyles,
        backgrounds: backgroundResponse?.patientBackgrounds,
        isNewUi: true,
      },
      emptyState: false,
      isFetching: backgroundFetching,
    },
  };

  // Split cards into data and empty state arrays
  const cardsWithData = [];
  const emptyCards = [] as {
    key: string;
    component: React.ElementType;
    props: Record<string, unknown>;
    isFetching: boolean;
  }[];

  for (const key of cardOrder) {
    const config = cardConfigs[key];
    if (config.hasData) {
      cardsWithData.push({ ...config, key });
    }
    else if (config.emptyState) {
      emptyCards.push({ ...config, key });
    }
  }

  // Order empty cards: medications, labs, hospitalization, allergies, diagnosis
  const emptyCardOrder = ['medications', 'labs', 'hospitalization', 'allergies', 'diagnosis'];
  const orderedEmptyCards = emptyCardOrder
    .map((key) => emptyCards.find((card) => card.key === key))
    .filter(Boolean);

  // Merge cards: data cards first, then ordered empty cards
  const finalCards = [...cardsWithData, ...orderedEmptyCards];

  // Remove medicationHistory from finalCards after Row 2
  const rows = [];
  let cardIdx = 0;

  // Row 1
  // Always take the first two non-medicationHistory cards for Row 1
  const firstRowCards = finalCards.filter((card) => card?.key !== 'medicationHistory').slice(0, 2);
  rows.push(firstRowCards);

  // Row 2 (medicationHistory only if present)
  let medicationHistoryCard = null;
  if (cardConfigs.medicationHistory.hasData) {
    medicationHistoryCard = cardsWithData.find((card) => card.key === 'medicationHistory');
    rows.push([medicationHistoryCard]);
  }

  // Remove medicationHistory from the card list for subsequent rows
  const filteredFinalCards = finalCards.filter((card) => card?.key !== 'medicationHistory');
  // Start after the first two cards (Row 1) and medicationHistory (Row 2)
  cardIdx = 2;
  if (medicationHistoryCard) cardIdx += 1;

  // Row 3
  rows.push(
    filteredFinalCards.slice(
      cardIdx - (medicationHistoryCard ? 1 : 0),
      cardIdx - (medicationHistoryCard ? 1 : 0) + 2,
    ),
  );
  cardIdx += 2;

  // Row 4 (now 3 cards)
  rows.push(
    filteredFinalCards.slice(
      cardIdx - (medicationHistoryCard ? 1 : 0),
      cardIdx - (medicationHistoryCard ? 1 : 0) + 3,
    ),
  );
  cardIdx += 3;

  // Row 5
  rows.push(
    filteredFinalCards.slice(
      cardIdx - (medicationHistoryCard ? 1 : 0),
      cardIdx - (medicationHistoryCard ? 1 : 0) + 2,
    ),
  );

  // Grid size assignment based only on row index
  const getGridSize = (rowIdx: number, row: Array<{ key: string }>) => {
    const medicationHistoryHasData = cardConfigs.medicationHistory.hasData;
    if (row.length === 1) {
      return 12;
    }
    switch (rowIdx) {
      case 0: // Row 1: 2 cards
        return 6;
      case 1: // Row 2: 1 card (medication history)
        return medicationHistoryHasData ? 12 : 6;
      case 2: // Row 3: 2 cards
        return 6;
      case 3: // Row 4: 3 cards
        return 4;
      case 4: // Row 5: 2 cards
        return 6;
      default:
        return 12;
    }
  };

  if (isFetchingHR) {
    return <LoadingState />;
  }

  return (
    <Box pt={1.5}>
      {refreshStatus === HrRefreshHistoryStatus.IN_PROGRESS && (
        <Box mb={1.5}>
          <LoadingState />
        </Box>
      )}
      <ShowMoreDrawer drawerItem={drawerItem} closeDrawer={closeDrawer}>
        <DrawerContent
          drawerItem={drawerItem}
          drawerChildProps={drawerChildProps}
          drawerTabIndex={drawerTabIndex}
          currentSymptomsResponse={currentSymptomsResponse}
          symptomsResponse={symptomsResponse}
          labsResponse={labsResponse}
          drawerData={drawerData}
          diagnosisResponse={diagnosisResponse}
          familyHistoryResponse={familyHistoryResponse}
          providerResponse={providerResponse}
          hospitalizationsResponse={hospitalizationsResponse}
          allergiesResponse={allergiesResponse}
          setIsOpenedFromChart={setIsOpenedFromChart}
          setSelectedMedication={setSelectedMedication}
          toggleDrawer={toggleDrawer}
          onShowMoreMedication={onShowMoreMedication}
        />
      </ShowMoreDrawer>
      <Grid container spacing={1}>
        <RefillsDrawer
          toggleDrawer={toggleDrawer}
          isOpen={showDrawer}
          medicationId={
            isOpenedFromChart
              ? medicationFromChart?.medicationId || ''
              : selectedMedication?.id ?? ''
          }
          medicationName={medicationName}
          medicationMetaId={
            isOpenedFromChart
              ? medicationFromChart?.medicationMetaId || ''
              : selectedMedication?.medicationMetaId ?? ''
          }
          patientId={isOpenedFromChart ? medicationFromChart?.patientId || '' : id || ''}
        />
        {rows.map((row, rowIdx) => {
          const filteredRow = row.filter(Boolean);
          return (
            <React.Fragment key={filteredRow.map((card) => card?.key).join('-')}>
              {filteredRow.map((card) => (card?.isFetching ? (
                <Spinner key={card?.key} />
              ) : (
                React.createElement(card?.component as React.ElementType, {
                  ...card?.props,
                  gridSize: getGridSize(rowIdx, filteredRow as Array<{ key: string }>),
                  key: card?.key,
                })
              )))}
            </React.Fragment>
          );
        })}
      </Grid>
    </Box>
  );
}
